import React, { useState, useEffect, useCallback } from "react";
import SelectMenu from "../common/forms/selectmenu";
import LabelInput from "../common/forms/Labelinput";
import { API_CONFIG, getHeaders, refreshAccessToken } from "../../config/api";
import { useLoader } from "../../context/loadercontext";
import Button from "../common/ui/button";
import { MdEditSquare } from "react-icons/md";
import { RiDeleteBin5Line } from "react-icons/ri";
import { useAuth } from "../../context/AuthContext";
import NoFound from "../common/ui/NoFound";
import { DeletePreviewModal, FinalDeleteConfirmModal } from "../common/ui/deletemodal";

export default function AddCalculationEntryForm() {
  const [materials, setMaterials] = useState([]);
  const [currentEntry, setCurrentEntry] = useState({
    material: "",
    weight: "",
    invoiceValue: "",
    exchangeRate: "",
    dutyRate: "",
    socialdutyRate: "",
    igstRate: "",
  });
  const [entries, setEntries] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const { setLoading } = useLoader();
  const { logout } = useAuth();
  const [showDeletePreview, setShowDeletePreview] = useState(false);
  const [showFinalDeleteConfirm, setShowFinalDeleteConfirm] = useState(false);
  const [deleteMode, setDeleteMode] = useState(null);
  const [pendingDeleteData, setPendingDeleteData] = useState(null);

  // 📦 Fetch materials
  const fetchMaterials = useCallback(async () => {
    setLoading(true);
    try {
      let response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.GET_ALL}`,
        { method: "GET", headers: getHeaders(true) }
      );

      // -------------------------
      // 🔥 If ACCESS TOKEN expired
      // -------------------------
      if (response.status === 401) {
        console.warn("Access token expired. Attempting refresh...");

        const newToken = await refreshAccessToken();

        if (newToken) {
          // Retry API call with new token
          response = await fetch(
            `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.MATERIAL.GET_ALL}`,
            { method: "GET", headers: getHeaders(true) }
          );
        } else {
          console.error("Refresh token failed. Logging out...");
          logout(); // redirect user
          return;
        }
      }
      const result = await response.json();
      if (result.status === 200) {
        setMaterials(
          result.data.map((m) => ({
            label: `${m.short_code.toUpperCase()}`,
            value: m.id,
            shortCode: m.short_code,
            basicDuty: m.basic_duty_pr,
            socialDuty: m.social_duty_pr,
            igst: m.igst_pr,
            nameOnly: m.name
          }))
        );
      }
    } finally {
      setLoading(false);
    }
  }, [setLoading, logout]);

  useEffect(() => {
    fetchMaterials();
  }, [fetchMaterials]);
  const handleCancelPreview = () => {
    setShowDeletePreview(false);
    setPendingDeleteData(null);
    setDeleteMode(null);
  };

  const handleProceedToFinalConfirm = () => {
    setShowDeletePreview(false);
    setShowFinalDeleteConfirm(true);
  };


  const updateCurrentEntry = (field, value) => {
    setCurrentEntry((prev) => ({ ...prev, [field]: value }));
  };

  // 💰 Calculation Logic
  const calculateValues = (entry) => {
    const weight = parseFloat(entry.weight) || 0;
    const invoiceValue = parseFloat(entry.invoiceValue) || 0;
    const exchangeRate = parseFloat(entry.exchangeRate) || 0;
    const dutyRate = parseFloat(entry.dutyRate) || 0;
    const socialDutyRate = parseFloat(entry.socialdutyRate) || 0;
    const igstRate = parseFloat(entry.igstRate) || 0;

    const assessmentValue = weight * invoiceValue * exchangeRate;
    const basicDuty = weight * invoiceValue * (dutyRate / 100) * exchangeRate;
    const socialWelfare = basicDuty * (socialDutyRate / 100);
    const igst =
      (assessmentValue + basicDuty + socialWelfare) * (igstRate / 100);
    const totalDuty = basicDuty + socialWelfare + igst;
    const usdInvoice = weight * invoiceValue;

    return {
      assessmentValue,
      basicDuty,
      socialWelfare,
      igst,
      totalDuty,
      usdInvoice,
    };
  };

  // ➕ Add or Update Entry
  const addNewEntry = () => {
    const selectedMaterial = materials.find(
      (m) => m.value === currentEntry.material
    );

    if (!selectedMaterial) return;

    const calc = calculateValues({
      ...currentEntry,
      dutyRate: selectedMaterial?.basicDuty || 0,
      socialdutyRate: selectedMaterial?.socialDuty || 0,
      igstRate: selectedMaterial?.igst || 0,
    });

    const completeEntry = {
      ...currentEntry,
      materialName: selectedMaterial?.nameOnly || "Unknown",
      shortCode: selectedMaterial?.shortCode || "--",
      dutyRate: selectedMaterial?.basicDuty.toFixed(2) || 0,
      socialdutyRate: selectedMaterial?.socialDuty.toFixed(2) || 0,
      igstRate: selectedMaterial?.igst.toFixed(2) || 0,
      assessmentValue: calc.assessmentValue.toFixed(2),
      basicDuty: calc.basicDuty.toFixed(2),
      socialWelfare: calc.socialWelfare.toFixed(2),
      igst: calc.igst.toFixed(2),
      totalDuty: calc.totalDuty.toFixed(2),
      usdInvoice: calc.usdInvoice.toFixed(2),
    };

    if (editingIndex !== null) {
      const updated = [...entries];
      updated[editingIndex] = completeEntry;
      setEntries(updated);
      setEditingIndex(null);
    } else {
      setEntries((prev) => [...prev, completeEntry]);
    }

    // Reset fields
    setCurrentEntry({
      material: "",
      weight: "",
      invoiceValue: "",
      exchangeRate: currentEntry.exchangeRate,
      dutyRate: "",
      socialdutyRate: "",
      igstRate: "",
    });
  };

  // ✏️ Edit Entry
  const handleEdit = (index) => {
    const entry = entries[index];
    if (!entry) return;
    const matchedMaterial = materials.find((m) => {
      return (
        m.materialName?.trim()?.toLowerCase() === entry.materialName?.trim()?.toLowerCase() ||
        m.label?.trim()?.toLowerCase() === entry.shortCode?.trim()?.toLowerCase()
      );
    });
    setCurrentEntry({
      material: matchedMaterial?.value || "",
      weight: entry.weight,
      invoiceValue: entry.invoiceValue,
      exchangeRate: entry.exchangeRate || currentEntry.exchangeRate,
      dutyRate: entry.dutyRate,
      socialdutyRate: entry.socialdutyRate,
      igstRate: entry.igstRate,
    });

    setEditingIndex(index);
  };
  const openDeletePreview = (index) => {
    const entry = entries[index];

    setPendingDeleteData({
      type: "entry",
      index,
      child: {
        shortCode: entry.shortCode,
        weight: entry.weight,
        invoiceValue: entry.invoiceValue,
        assessmentValue: entry.assessmentValue,
        basicDuty: entry.basicDuty,
        socialWelfare: entry.socialWelfare,
        igst: entry.igst,
        totalDuty: entry.totalDuty,
        usdInvoice: entry.usdInvoice,
      },
    });

    setDeleteMode("entry");
    setShowDeletePreview(true);
  };



  // 🗑️ Delete Entry
  const handleDelete = () => {
    if (deleteMode === "entry") {
      const index = pendingDeleteData.index;

      const updated = entries.filter((_, i) => i !== index);
      setEntries(updated);

      if (editingIndex === index) {
        setEditingIndex(null);
      }
    }

    setShowFinalDeleteConfirm(false);
    setPendingDeleteData(null);
    setDeleteMode(null);
  };


  // 🧮 Totals Summary
  const totals = entries.reduce(
    (acc, entry) => {
      const calc = calculateValues(entry);
      acc.totalWeight += parseFloat(entry.weight) || 0;
      acc.totalAssessment += calc.assessmentValue || 0;
      acc.totalBasicDuty += calc.basicDuty || 0;
      acc.totalSocial += calc.socialWelfare || 0;
      acc.totalIGST += calc.igst || 0;
      acc.totalDuty += calc.totalDuty || 0;
      acc.totalUsdInvoice += calc.usdInvoice || 0;
      return acc;
    },
    {
      totalWeight: 0,
      totalAssessment: 0,
      totalBasicDuty: 0,
      totalSocial: 0,
      totalIGST: 0,
      totalDuty: 0,
      totalUsdInvoice: 0,
    }
  );

  const totalPayableDuty =
    totals.totalBasicDuty + totals.totalSocial + totals.totalIGST;
  const igstAssValue =
    totals.totalAssessment + totals.totalBasicDuty + totals.totalSocial;

  const formatIndianNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return "0";
    return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <>
      <div className="space-y-6 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-6 items-start">
          {/* === Left Panel === */}
          <div className="lg:col-span-4 col-span-12 bg-white shadow-md border border-gray-100 rounded p-5 space-y-5">
            {/* Exchange Rate */}
            <div className="grid grid-cols-12 items-center gap-2 sm:gap-3">
              <label className="col-span-5 text-md font-semibold p-2">
                Exchange Rate
              </label>
              <label className="col-span-1 text-lg font-semibold p-1">₹</label>
              <div className="col-span-6">
                <input
                  type="text"
                  value={currentEntry.exchangeRate}
                  onChange={(e) =>
                    updateCurrentEntry("exchangeRate", e.target.value)
                  }
                  required
                  className="w-full border-2 border-gray-200 rounded-md px-3 py-2 text-[17px] font-bold text-gray-800 bg-white focus:outline-none h-9 transition-all"
                />
              </div>
            </div>

            <SelectMenu
              label="Material"
              options={materials}
              value={currentEntry.material}
              onChange={(value) => updateCurrentEntry("material", value)}
              searchable
              required
              usePortal
            />

            <div className="grid grid-cols-2 gap-4">
              <LabelInput
                label="Weight (kg)"
                type="number"
                value={currentEntry.weight}
                onChange={(e) => updateCurrentEntry("weight", e.target.value)}
                required
              />
              <LabelInput
                label="Invoice Value ($)"
                type="number"
                value={currentEntry.invoiceValue}
                onChange={(e) =>
                  updateCurrentEntry("invoiceValue", e.target.value)
                }
                required
              />
            </div>

            <Button
              onClick={addNewEntry}
              disabled={
                !currentEntry.material ||
                !currentEntry.weight ||
                !currentEntry.invoiceValue
              }
              className="w-full h-8 text-xs font-medium px-3"
            >
              {editingIndex !== null ? "Update Entry" : "Add Entry"}
            </Button>

            {/* Totals Summary */}
            <div className="border-t border-dashed my-3"></div>

            <div className="space-y-3 text-xs">
              <div className="flex justify-between">
                <span className="text-gray-600 text-xs leading-none">
                  Total Payable Duty
                </span>
                <span className="text-gray-900 text-[19px] font-semibold leading-none">
                  ₹ {formatIndianNumber(totalPayableDuty)}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="text-gray-600 text-xs leading-none">
                  IGST Ass. Value
                </span>
                <span className="text-gray-900 text-[19px] font-semibold leading-none">
                  ₹ {formatIndianNumber(igstAssValue)}
                </span>
              </div>
            </div>

            {/* Detailed Totals */}
            <div className="bg-gray-50 rounded p-3 space-y-2 text-xs border border-gray-100">
              {[
                ["Total Weight", formatIndianNumber(totals.totalWeight)],
                ["Total Assessment", formatIndianNumber(totals.totalAssessment)],
                ["Total Basic Duty", formatIndianNumber(totals.totalBasicDuty)],
                ["Total Social Duty", formatIndianNumber(totals.totalSocial)],
                ["Total IGST", formatIndianNumber(totals.totalIGST)],
                ["Total Duty", formatIndianNumber(totals.totalDuty)],
                ["Total USD Invoice", formatIndianNumber(totals.totalUsdInvoice)],
              ].map(([label, value]) => (
                <div
                  key={label}
                  className="flex justify-between items-center py-[1px] border-b border-gray-100 last:border-none"
                >
                  <span className="text-gray-600 text-xs leading-none">
                    {label}
                  </span>
                  <span className="text-gray-900 text-[15px] font-semibold leading-none">
                    ₹ {value}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* === Right Panel (Table Section) === */}
          <div className="col-span-8 pt-5">
            <div className="w-full">
              <div className="overflow-x-auto">
                <table className="w-full text-[11px] text-center">
                  <thead className="h-10 border-b border-gray-200 text-gray-600 font-medium">
                    <tr>
                      <th>Material</th>
                      <th>Weight</th>
                      <th>Invoice Value</th>
                      <th>Assessment Value</th>
                      <th>Basic Duty</th>
                      <th>Social Duty</th>
                      <th>IGST</th>
                      <th>Total Duty</th>
                      <th>USD Invoice</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="p-6 text-gray-400">
                          <NoFound />
                        </td>
                      </tr>
                    ) : (
                      entries.map((entry, i) => (
                        <tr
                          key={i}
                          className="border-t h-10 border-gray-100 hover:bg-gray-50"
                        >
                          <td>{entry.shortCode ? entry.shortCode.toUpperCase() : "--"}</td>
                          <td>{formatIndianNumber(entry.weight)}</td>
                          <td>{formatIndianNumber(entry.invoiceValue)}</td>
                          <td>{formatIndianNumber(entry.assessmentValue)}</td>
                          <td>{formatIndianNumber(entry.basicDuty)}</td>
                          <td>{formatIndianNumber(entry.socialWelfare)}</td>
                          <td>{formatIndianNumber(entry.igst)}</td>
                          <td>{formatIndianNumber(entry.totalDuty)}</td>
                          <td>{formatIndianNumber(entry.usdInvoice)}</td>
                          <td>
                            <div className="flex items-center justify-center space-x-1">
                              <button
                                onClick={() => handleEdit(i)}
                                className="text-green-500 hover:text-green-700 p-1"
                                title="Edit Entry"
                              >
                                <MdEditSquare size={18} />
                              </button>
                              <button
                                onClick={() => openDeletePreview(i)}
                                className="text-red-500 hover:text-red-700 p-1"
                                title="Delete Entry"
                              >
                                <RiDeleteBin5Line size={18} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Modals */}
      {/* Delete preview modal for parent or child */}
      {showDeletePreview && pendingDeleteData && (
        <DeletePreviewModal
          title="Entry Details"
          data={{
            "Material": pendingDeleteData.child?.shortCode?.toUpperCase(),
            "Weight (kg)": pendingDeleteData.child?.weight,
            "Invoice Value ($)": pendingDeleteData.child?.invoiceValue,
            "Assessment Value": pendingDeleteData.child?.assessmentValue,
            "Basic Duty": pendingDeleteData.child?.basicDuty,
            "Social Duty": pendingDeleteData.child?.socialWelfare,
            "IGST": pendingDeleteData.child?.igst,
            "Total Duty": pendingDeleteData.child?.totalDuty,
            "USD Invoice": pendingDeleteData.child?.usdInvoice,
          }}
          onCancel={handleCancelPreview}
          onProceed={handleProceedToFinalConfirm}
        />
      )}


      {/* Final delete confirm modal */}
      {showFinalDeleteConfirm && pendingDeleteData && (
        <FinalDeleteConfirmModal
          title="Confirm Delete"
          message="Are you sure you want to permanently delete this entry?"
          onCancel={() => {
            setShowFinalDeleteConfirm(false);
            setPendingDeleteData(null);
            setDeleteMode(null);
          }}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
}
